import { apiFetch } from '@/lib/api';

export interface BillingSubscription {
  tier: string;
  status: string;
  customer_id: string | null;
  subscription_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export async function getBillingSubscription(verticalId = 'platform'): Promise<BillingSubscription> {
  const params = new URLSearchParams({ vertical_id: verticalId });
  return apiFetch<BillingSubscription>(`/api/v1/billing/subscription?${params.toString()}`);
}

/** Authoritative per-(tenant, vertical) access decision (the admit rule lives in the backend). */
export interface BillingAccessDecision {
  vertical_id: string;
  admitted: boolean;
  subscription_status: string;
  tier: string;
  effective_tier: string;
  reason: string;
}

export async function getBillingAccess(verticalId: string): Promise<BillingAccessDecision> {
  const params = new URLSearchParams({ vertical_id: verticalId });
  return apiFetch<BillingAccessDecision>(`/api/v1/billing/access?${params.toString()}`);
}

/**
 * Which of the caller's tenant memberships is admitted to a vertical — the
 * explicit tenant selection for a vertical door (HENNIPENNY-LAB-ACTIVATION-1).
 * `tenant_id` is the engine tenant SLUG the whole substrate keys on.
 */
export interface VerticalTenantResolution {
  vertical_id: string;
  admitted: boolean;
  tenant_id: string | null;
  display_name: string | null;
  membership_role: string | null;
  memberships_checked: number;
  reason: string;
}

export async function resolveVerticalTenant(verticalId: string): Promise<VerticalTenantResolution> {
  const params = new URLSearchParams({ vertical_id: verticalId });
  return apiFetch<VerticalTenantResolution>(`/api/v1/billing/vertical-tenant?${params.toString()}`);
}

/** One customer vertical the caller is admitted to, with the registry home. */
export interface CustomerVerticalLanding {
  vertical_id: string;
  display_name: string;
  customer_home_url: string;
  tenant_id: string;
  tenant_display_name: string | null;
  membership_role: string | null;
}

/**
 * Where a BARE-login (no return_to) caller should land. Reuses the same
 * cross-membership resolution as resolveVerticalTenant across the registry's
 * customer verticals, so a lab MEMBER who owns a personal tenant is offered
 * their vertical instead of the platform dashboard. `redirect_url` is set ONLY
 * when it is unambiguous and safe (a non-operator whose SOLE membership is
 * admitted to exactly one customer vertical); otherwise show the Continue
 * action(s) from `verticals`. Operators/Directors get `is_operator: true` with
 * no verticals — they keep today's dashboard behavior and are never redirected.
 */
export interface PostLoginLanding {
  verticals: CustomerVerticalLanding[];
  memberships_checked: number;
  is_operator: boolean;
  redirect_url: string | null;
}

export async function resolvePostLoginLanding(): Promise<PostLoginLanding> {
  return apiFetch<PostLoginLanding>('/api/v1/billing/post-login-landing');
}

/**
 * Activate a vertical's FREE ($0) tier for the signed-in tenant. The same door
 * the operator-approval path uses; requires an approved application server-side
 * (returns 400/403 otherwise). Paid tiers go through createCheckout, not here.
 */
export async function activateFreeTier(verticalId: string): Promise<BillingAccessDecision> {
  return apiFetch<BillingAccessDecision>('/api/v1/billing/activate-free', {
    method: 'POST',
    body: JSON.stringify({ vertical_id: verticalId }),
  });
}

export interface CheckoutResponse {
  checkout_url: string;
  session_id: string | null;
}

/**
 * Start a paid checkout for a vertical tier. Resolves the Paddle price from
 * pricing_tiers by (vertical_id, tier_key); returns 400 if no Paddle price is
 * configured for that tier yet.
 */
export async function createCheckout(params: {
  verticalId: string;
  tier: string;
  successUrl: string;
  cancelUrl: string;
}): Promise<CheckoutResponse> {
  return apiFetch<CheckoutResponse>('/api/v1/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({
      vertical_id: params.verticalId,
      tier: params.tier,
      success_url: params.successUrl,
      cancel_url: params.cancelUrl,
    }),
  });
}

/** Mint a fresh portal URL for the authenticated tenant's selected vertical. */
export async function createBillingPortal(verticalId: string, returnUrl: string): Promise<{ url: string }> {
  return apiFetch<{ url: string }>('/api/v1/billing/portal', {
    method: 'POST', body: JSON.stringify({ vertical_id: verticalId, return_url: returnUrl }),
  });
}
